import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import PublicHeader from "@/components/layout/PublicHeader";
import PublicFooter from "@/components/layout/PublicFooter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, ExternalLink, BookOpen, KeyRound } from "lucide-react";
import { motion } from "framer-motion";
import "@/i18n/fase5";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function DatosAbertos() {
  const { t, i18n } = useTranslation();
  const [datasets, setDatasets] = useState([]);
  const lang = i18n.language;

  useEffect(() => {
    fetch(`${BACKEND}/api/open/datasets`).then((r) => r.json()).then((d) => setDatasets(d.datasets || []));
  }, []);

  const dl = (code, fmt) => {
    window.open(`${BACKEND}/api/open/datasets/${code}/download?format=${fmt}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-salnes-ivory" data-testid="page-datos-abertos">
      <PublicHeader />
      <section className="relative pt-32 pb-16 border-b hairline">
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "url(/brand/hero-salnes-alt1.jpg)", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="relative mx-auto max-w-[1100px] px-6 lg:px-10">
          <div className="text-[11px] uppercase tracking-[0.28em] text-salnes-emerald mb-3">CC BY 4.0 · {t("common.institutional")}</div>
          <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="font-serif text-5xl lg:text-6xl text-salnes-ink leading-[1.02]">
            {t("fase5.od_title")}
          </motion.h1>
          <p className="mt-6 text-lg text-salnes-ink/80 leading-relaxed max-w-2xl">{t("fase5.od_subtitle")}</p>
          <p className="mt-3 text-sm text-salnes-stone max-w-3xl">{t("fase5.od_intro")}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href={`${BACKEND}/api/open/docs`} target="_blank" rel="noreferrer">
              <Button variant="outline" className="rounded-full" data-testid="od-api-docs-btn"><BookOpen size={14} className="mr-2" />{t("fase5.od_docs_api")}</Button>
            </a>
            <a href="mailto:datos@salnes.gal" data-testid="od-request-key-link">
              <Button variant="outline" className="rounded-full"><KeyRound size={14} className="mr-2" />{t("fase5.od_get_key")}</Button>
            </a>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-[1100px] px-6 lg:px-10">
          <div className="text-[11px] uppercase tracking-[0.24em] text-salnes-stone mb-6">{t("fase5.od_datasets")}</div>
          <div className="grid md:grid-cols-2 gap-5" data-testid="od-datasets-grid">
            {datasets.map((ds) => (
              <motion.div
                key={ds.code}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                data-testid={`od-dataset-${ds.code}`}
                className="rounded-2xl border hairline bg-white p-6 hover:shadow-md transition-all"
              >
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Badge className="bg-salnes-emerald text-white text-[10px]">{ds.license}</Badge>
                  <Badge variant="outline" className="text-[10px]">{ds.frequency}</Badge>
                </div>
                <div className="font-serif text-xl text-salnes-ink">{ds[`title_${lang}`] || ds.title_gl}</div>
                <div className="text-sm text-salnes-stone mt-2 leading-relaxed">{ds[`description_${lang}`] || ds.description_gl}</div>
                <div className="text-[11px] text-salnes-stone mt-3 italic">&ldquo;{ds[`citation_suggested_${lang}`] || ds.citation_suggested_gl}&rdquo;</div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {(ds.formats_available || []).map((f) => (
                    <Button key={f} size="sm" variant="outline" onClick={() => dl(ds.code, f)} data-testid={`od-download-${ds.code}-${f}`} className="rounded-full">
                      <Download size={12} className="mr-1" /> {f.toUpperCase()}
                    </Button>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-16 rounded-2xl border hairline bg-white p-8">
            <div className="text-[11px] uppercase tracking-[0.22em] text-salnes-stone mb-2">{t("fase5.od_how_title")}</div>
            <div className="font-serif text-2xl text-salnes-ink mb-3">API pública</div>
            <div className="text-sm text-salnes-stone mb-4">{t("fase5.od_how_body")}</div>
            <pre className="bg-salnes-ink text-salnes-ivory p-4 rounded-lg text-[12px] overflow-auto">
{`curl "${BACKEND}/api/open/datasets"

curl -H "X-API-Key: <sua-chave>" \\
  "${BACKEND}/api/open/datasets/indicadores-esg-salnes/download?format=csv"`}
            </pre>
            <div className="mt-3">
              <Link to="/documentacion" className="text-sm text-salnes-atlantic underline inline-flex items-center gap-1">
                <ExternalLink size={12} /> {t("fase5.doc_title")}
              </Link>
            </div>
          </div>
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}
